import { NgIf, NgFor, AsyncPipe } from '@angular/common'
import { Component } from '@angular/core'
import { RouterLink } from '@angular/router'
import { BreadcrumbMeta, CmsService, PageMetaService, TranslationService } from '@spartacus/core'
import { CurrentProductService, IconComponent } from '@spartacus/storefront'
import { combineLatest, Observable } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import {
  EbProduct,
  EbProductTypeEnum
} from '../../features/product/interfaces/eb-product.interface'
import { EbLanguageService } from '../../features/site-context/services/eb-language.service'

@Component({
  selector: 'eb-breadcrumb',
  templateUrl: './eb-breadcrumb.component.html',
  styleUrls: ['./eb-breadcrumb.component.scss'],
  imports: [NgIf, NgFor, IconComponent, RouterLink, AsyncPipe]
})
export class EbBreadcrumbComponent {
  constructor(
    public pageMetaService: PageMetaService,
    protected languageService: EbLanguageService,
    protected currentProductService: CurrentProductService,
    protected translation: TranslationService,
    protected cms: CmsService
  ) {}

  get pageMeta$(): Observable<{
    title: string
    breadcrumbs: BreadcrumbMeta[]
    product?: EbProduct
  }> {
    return combineLatest([
      this.pageMetaService.getMeta(),
      this.translation.translate('common.home'),
      this.languageService.getActive(),
      this.cms.getCurrentPage(),
      this.currentProductService.getProduct()
    ]).pipe(
      distinctUntilChanged((prev, curr) => {
        const prevMeta = prev[0]
        const currMeta = curr[0]
        const prevPage = prev[3]
        const currPage = curr[3]
        const prevProduct = prev[4]
        const currProduct = curr[4]

        return (
          prevPage?.pageId === currPage?.pageId &&
          JSON.stringify(prevMeta?.breadcrumbs) === JSON.stringify(currMeta?.breadcrumbs) &&
          prevProduct?.code === currProduct?.code
        )
      }),
      map(([meta, textHome, activeLanguage, page, product]) => {
        const ebProduct = product as EbProduct
        const breadcrumbs = meta?.breadcrumbs ? [...meta.breadcrumbs] : []
        const defaultCrumb = { label: textHome, link: '/' }

        if (page?.pageId === 'ebWebshopHomepage') {
          return {
            title: meta.heading || meta.title,
            breadcrumbs: [],
            product
          }
        }

        if (breadcrumbs.length > 0 && !breadcrumbs.some(crumb => crumb.label === textHome)) {
          breadcrumbs.unshift(defaultCrumb)
        } else if (breadcrumbs.length === 0) {
          breadcrumbs.push(defaultCrumb)
        }

        if (page?.pageId === 'ebProductDetails' && ebProduct) {
          let productCrumb
          if (this.isVariant(product)) {
            productCrumb = {
              link: this.getParentUrl(product),
              label: ebProduct.parentProductName || ebProduct.name
            }
          } else {
            productCrumb = {
              link: '',
              label: ebProduct.name
            }
          }
          const index = this.getIndexForProductBreadcrumb(breadcrumbs, productCrumb)
          index >= 0 ? (breadcrumbs[index] = productCrumb) : breadcrumbs.push(productCrumb)
        }

        return {
          title: meta.heading || meta.title,
          breadcrumbs,
          product
        }
      })
    )
  }

  private getParentUrl(product): string {
    return `/${product.parentProductFullPath}/p/${product.parentProductId}`
  }

  private isVariant(product): boolean {
    return product?.ebProductType === EbProductTypeEnum.VARIANT
  }

  private getIndexForProductBreadcrumb(
    breadcrumbs: BreadcrumbMeta[],
    productCrumb: BreadcrumbMeta
  ): number {
    return breadcrumbs.findIndex(el => el.label === productCrumb.label)
  }
}
